import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Result,
  Row,
  Typography,
} from "antd";
import axios from "axios";

type TuerchenContent = {
  tag: string;
  frage: string;
  zu_gewinnen_lang: string;
  zu_gewinnen_bild_url?: string;
};

type TeilnahmeModalProps = {
  open: boolean;
  onClose: () => void;
  content?: TuerchenContent;
  teilnahmebedingungenUrl?: string;
  teilnahmebedingungenEmail?: string;
};

export default function TeilnahmeModal({
  open,
  onClose,
  content,
  teilnahmebedingungenEmail,
  teilnahmebedingungenUrl,
}: TeilnahmeModalProps) {
  const [loading, setLoading] = useState(false);
  const [antwort, setAntwort] = useState("");
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [plz, setPlz] = useState("");
  const [strasse, setStrasse] = useState("");
  const [ort, setOrt] = useState("");
  const [email, setEmail] = useState("");
  const [teilnahmebedingungenChecked, setTeilnahmebedingungenChecked] =
    useState(false);
  const [bereitsTeilgenommen, setBereitsTeilgenommen] = useState(false);

  useEffect(() => {
    const tagVonLetzterTeilnahme = localStorage.getItem(
      "TAG_ZULETZT_TEILGENOMMEN"
    );

    if (tagVonLetzterTeilnahme != null) {
      const t = Number(tagVonLetzterTeilnahme);
      const d = Number(content?.tag);

      if (t === d) {
        setBereitsTeilgenommen(true);
      } else {
        setBereitsTeilgenommen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmitTeilnahme = async () => {
    setLoading(true);
    const {
      success,
      error,
      message: m,
    } = (
      await axios.post(
        `${process.env.REACT_APP_SUB_DIR}api/public/teilnahme.php`,
        {
          firstName: vorname,
          lastName: nachname,
          plz,
          ort,
          strasse,
          email,
          answer: antwort,
          tag: content?.tag,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
    ).data;

    setLoading(false);

    if (success) {
      Modal.success({ content: m });
      localStorage.setItem(
        "TAG_ZULETZT_TEILGENOMMEN",
        content ? content.tag : "0"
      );
      setBereitsTeilgenommen(true);
    } else {
      Modal.error({ content: error });
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={840}
      className="teilnahme-modal"
      centered
      maskStyle={{ opacity: 1 }}
    >
      {bereitsTeilgenommen ? (
        <Result
          status="success"
          title="Vielen Dank! Du hast heute bereits teilgenommen."
        />
      ) : (
        <>
          <Typography.Title level={4}>
            <b>Zu gewinnen</b>
          </Typography.Title>

          <Row className="gewinnspiel-beschreibung" gutter={[24, 24]}>
            {content?.zu_gewinnen_bild_url && (
              <Col xs={24} md={8} className="bild">
                <img
                  src={content?.zu_gewinnen_bild_url}
                  alt="Bild für Gewinnspiel"
                />
              </Col>
            )}
            <Col xs={24} md={16} className="text">
              {content?.zu_gewinnen_lang}
            </Col>
          </Row>

          <Divider type="horizontal" style={{ margin: "24px 0" }} />

          <Typography.Title level={5}>
            Für die Teilnahme beantworte bitte folgende Frage:
          </Typography.Title>
          <Typography.Title level={3}>{content?.frage}</Typography.Title>
          <Input
            placeholder="Schreibe hier deine Antwort"
            size="large"
            value={antwort}
            onChange={(e) => {
              setAntwort(e.target.value);
            }}
          />

          <Divider type="horizontal" style={{ margin: "24px 0" }} />

          <Typography.Title level={5}>
            Zum Abschluss benötigen wir noch ein paar persönliche Informationen:
          </Typography.Title>
          <Form
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            validateTrigger={"onBlur"}
          >
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="firstName"
                  label="Vorname"
                  rules={[
                    {
                      required: true,
                      type: "string",
                      message: "Bitte einen Vornamen angeben!",
                    },
                  ]}
                >
                  <Input
                    value={vorname}
                    onChange={(e) => {
                      setVorname(e.target.value);
                    }}
                    onBlur={(e) => {
                      setVorname(e.target.value.trim());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="lastName"
                  label="Nachname"
                  rules={[
                    {
                      required: true,
                      type: "string",
                      message: "Bitte einen Nachnamen angeben!",
                    },
                  ]}
                >
                  <Input
                    value={nachname}
                    onChange={(e) => {
                      setNachname(e.target.value);
                    }}
                    onBlur={(e) => {
                      setNachname(e.target.value.trim());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="email"
                  label="E-Mail Adresse"
                  rules={[
                    {
                      required: true,
                      type: "email",
                      message: "Bitte eine gültige E-Mail Adresse angeben!",
                    },
                  ]}
                >
                  <Input
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    onBlur={(e) => {
                      setEmail(e.target.value.trim());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="strasse"
                  label="Straße &amp; Nr."
                  rules={[
                    {
                      required: true,
                      type: "string",
                      message:
                        "Bitte eine gültige Adresse mit Hausnummer angeben!",
                    },
                  ]}
                >
                  <Input
                    value={strasse}
                    onChange={(e) => {
                      setStrasse(e.target.value);
                    }}
                    onBlur={(e) => {
                      setStrasse(e.target.value.trim());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="plz"
                  label="PLZ"
                  rules={[
                    {
                      required: true,
                      type: "string",
                      message:
                        "Bitte eine gültige, vierstellige Postleitzahl angeben!",
                      pattern: /^[0-9]{4}$/,
                    },
                  ]}
                >
                  <Input
                    value={plz}
                    onChange={(e) => {
                      setPlz(e.target.value);
                    }}
                    onBlur={(e) => {
                      setPlz(e.target.value.trim());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="ort"
                  label="Ort"
                  rules={[
                    {
                      required: true,
                      type: "string",
                      message: "Bitte einen gültigen Ort angeben!",
                    },
                  ]}
                >
                  <Input
                    value={ort}
                    onChange={(e) => {
                      setOrt(e.target.value);
                    }}
                    onBlur={(e) => {
                      setOrt(e.target.value.trim());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Checkbox
                  checked={teilnahmebedingungenChecked}
                  onChange={(e) => {
                    setTeilnahmebedingungenChecked(e.target.checked);
                  }}
                />{" "}
                Ich akzeptiere die{" "}
                <a
                  href={
                    teilnahmebedingungenUrl ||
                    "https://www.shopping-horn.at/teilnahmebedingungen/"
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  Teilnahmebedingungen
                </a>
                .
              </Col>
            </Row>
            <input type="hidden" name="address" />
          </Form>
          <Row style={{ margin: "12px 0" }}>
            <Col xs={24}>
              <p className="teilnahmebedingungen-text">
                Teilnahme am Gewinnspiel ist immer nur am selben Tag von 0 bis
                24 Uhr möglich und pro Person nimmt nur ein korrekt ausgefülltes
                Gewinnspielformular an der Verlosung der Preise teil.
                <br />
                Mit der Teilnahme am Gewinnspiel akzeptiere ich die{" "}
                <a
                  href={
                    teilnahmebedingungenUrl ||
                    "https://www.shopping-horn.at/teilnahmebedingungen/"
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  Teilnahmebedingungen
                </a>{" "}
                und erkläre mich damit einverstanden, dass meine persönlichen
                Daten, gespeichert, automationsunterstützt verarbeitet und
                ausschließlich zu Werbezwecken der shoma marketing gmbh
                verwendet werden können! Ich kann meine Zustimmung jederzeit per
                E-Mail an{" "}
                <a
                  href={`mailto:${
                    teilnahmebedingungenEmail || "mailto:info@hanssonzentrum.at"
                  }`}
                >
                  {teilnahmebedingungenEmail || "info@hanssonzentrum.at"}
                </a>{" "}
                widerrufen. Im Falle meiner Ziehung als Gewinner stimme ich der
                Veröffentlichung meines Namens zu. Die Verlosung erfolgt unter
                Ausschluss des Rechtswegs.
                <br />
                Gewinne können nicht in bar abgelöst werden. Veranstalter des
                Gewinnspiels: shoma marketing gmbh, Makronweg 1, 3580
                Frauenhofen, Österreich.
              </p>
            </Col>
          </Row>
          <Row style={{ marginTop: "24px" }}>
            <Col xs={24}>
              <Button
                type="primary"
                onClick={handleSubmitTeilnahme}
                loading={loading}
                disabled={
                  !teilnahmebedingungenChecked ||
                  !vorname ||
                  !nachname ||
                  !email ||
                  !plz
                }
              >
                Absenden &amp; teilnehmen!
              </Button>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
}
